// src/components/dashboards/add_patient_v2/hooks/useCardsState.ts
// -----------------------------------------------------------------------------
// useCardsState — the single source of truth for the V2 cards UI.
//
// State invariants:
//   - The array is never empty while the form is mounted: removing the last
//     card resets it to a fresh empty card (one card minimum).
//   - Each card has a stable `id` for React keys and update operations.
//   - Submission state (loading, error) lives in the parent, not here.
//
// Design reference: cursor_logs/030 § 2026-05-22 cards-UI design.
// -----------------------------------------------------------------------------

import { useCallback, useMemo, useState } from "react";
import { PatientCardData } from "../types";

let _cardIdCounter = 0;
const nextCardId = () => `pcard-${Date.now()}-${++_cardIdCounter}`;

export const createEmptyCard = (
  overrides: Partial<PatientCardData> = {},
): PatientCardData => ({
  id: nextCardId(),
  name: "",
  externalId: "",
  description: "",
  files: [],
  collapsed: false,
  status: "idle",
  source: "manual",
  ...overrides,
});

/**
 * Is this card genuinely empty, i.e. safe to discard without losing anything
 * the user did?
 *
 * Deliberately strict. Every clause below is a way a card can LOOK blank while
 * still carrying state:
 *   - status        only an `idle` card is discardable; submitting / polling /
 *                   succeeded / failed cards are a record of work, never removed.
 *   - matchedPatient the card is in merge mode against an existing patient.
 *   - tabular       a Review&Edit card carries `tabularPromptText` (its real
 *                   payload) while name/files can both be blank.
 *   - externalIdPending an uncommitted draft is still sitting in the chip input,
 *                   so the user is mid-edit even though `externalId` is empty.
 *   - crossEnvLock  the card is waiting on an environment activation.
 */
export const isCardEmpty = (card: PatientCardData): boolean =>
  card.status === "idle" &&
  card.name.trim() === "" &&
  card.externalId.trim() === "" &&
  card.description.trim() === "" &&
  card.files.length === 0 &&
  !card.matchedPatient &&
  !card.tabularPromptText &&
  card.source !== "tabular" &&
  !card.externalIdPending &&
  !card.crossEnvLock;

/**
 * Drop the run of empty cards at the FRONT of the array and promote the first
 * card that actually has data.
 *
 * Why: a drag-and-drop lands N new cards after the pristine card the form
 * mounts with, leaving an empty box in first position. That box fails the
 * "every patient needs a name" check, so it disables Create-all until the user
 * clicks its ✕. Nothing about it is intentional, so it is removed for them.
 *
 * Pure, and conservative by construction:
 *   - only LEADING empties are touched; an empty card the user added in the
 *     middle or at the end is theirs to keep;
 *   - if no card has data (the normal starting state, or all-empty) the array
 *     is returned UNCHANGED, so the form never ends up with zero cards;
 *   - `isProtected` lets the caller veto a removal, used for the card the
 *     cursor is currently in.
 */
export const pruneEmptyLeadingCards = (
  cards: PatientCardData[],
  isProtected?: (card: PatientCardData) => boolean,
): PatientCardData[] => {
  let firstKept = 0;
  while (
    firstKept < cards.length &&
    isCardEmpty(cards[firstKept]) &&
    !isProtected?.(cards[firstKept])
  ) {
    firstKept += 1;
  }
  // firstKept === 0        → the first card has data; nothing to do.
  // firstKept >= length    → every card is empty; keep them, or the form would
  //                          be left with no card at all.
  if (firstKept === 0 || firstKept >= cards.length) return cards;
  return cards.slice(firstKept);
};

export interface UseCardsStateApi {
  cards: PatientCardData[];
  /** Replace a single card by id with a partial update. */
  updateCard: (id: string, patch: Partial<PatientCardData>) => void;
  /** Append files to a card by id (functional update — no stale closure).
   *  No-op when the card is succeeded / submitting / polling. Returns true
   *  if the files were accepted, false if the card was read-only/missing. */
  appendFilesToCard: (
    id: string,
    files: File[],
  ) => "ok" | "succeeded" | "midflight" | "noop";
  /** Append N new cards (default 1). Returns the ids of the added cards. */
  addCards: (cards?: Partial<PatientCardData>[]) => string[];
  /** Remove a card; if removing the last one, resets to a single empty card. */
  removeCard: (id: string) => void;
  /** Drop empty leading cards once a later card has data, so a pristine first
   *  card cannot block Create-all. No-op when nothing has data.
   *  `isProtected` vetoes removal (used to spare the focused card). */
  dropEmptyLeadingCards: (
    isProtected?: (card: PatientCardData) => boolean,
  ) => void;
  /** Reset the whole form to a single empty card (post-submit, post-error). */
  reset: () => void;
  /** Total file count across all cards. */
  totalFileCount: number;
  /** Cards in `idle` status — eligible for the global batch submit. */
  idleCards: PatientCardData[];
  /** True if every idle card has a non-empty name. */
  allIdleCardsValid: boolean;
  /** Deprecated alias — prefer allIdleCardsValid. */
  allCardsValid: boolean;
}

export function useCardsState(): UseCardsStateApi {
  const [cards, setCards] = useState<PatientCardData[]>(() => [createEmptyCard()]);

  const updateCard = useCallback(
    (id: string, patch: Partial<PatientCardData>) => {
      setCards((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      );
    },
    [],
  );

  // Used by the centralized drop router so a stale `cards` closure can't
  // drop files into the wrong card. `accepted` is read back synchronously
  // after the functional update via a module-local flag.
  // Returns WHY the drop was (not) accepted so the caller can show an accurate
  // message instead of a blanket "already created":
  //   "ok"        — files appended to an idle/failed card
  //   "succeeded" — patient already created → open its detail page
  //   "midflight" — still being created (submitting/polling) → wait
  //   "noop"      — no matching card or no files → stay silent (never a toast)
  const appendFilesToCard = useCallback(
    (id: string, files: File[]): "ok" | "succeeded" | "midflight" | "noop" => {
      if (files.length === 0) return "noop";
      let result: "ok" | "succeeded" | "midflight" | "noop" = "noop";
      setCards((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          if (c.status === "succeeded") {
            result = "succeeded";
            return c;
          }
          if (c.status === "submitting" || c.status === "polling") {
            result = "midflight";
            return c;
          }
          result = "ok";
          return { ...c, files: [...c.files, ...files] };
        }),
      );
      return result;
    },
    [],
  );

  const addCards = useCallback(
    (newCards: Partial<PatientCardData>[] = [{}]) => {
      const created = newCards.map((overrides) => createEmptyCard(overrides));
      setCards((prev) => [...prev, ...created]);
      return created.map((c) => c.id);
    },
    [],
  );

  const removeCard = useCallback((id: string) => {
    setCards((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      return filtered.length > 0 ? filtered : [createEmptyCard()];
    });
  }, []);

  const dropEmptyLeadingCards = useCallback(
    (isProtected?: (card: PatientCardData) => boolean) => {
      setCards((prev) => pruneEmptyLeadingCards(prev, isProtected));
    },
    [],
  );

  const reset = useCallback(() => {
    setCards([createEmptyCard()]);
  }, []);

  const totalFileCount = useMemo(
    () => cards.reduce((sum, c) => sum + c.files.length, 0),
    [cards],
  );

  // Only idle / failed cards are eligible for batch submit. Succeeded ones
  // are read-only; submitting/polling ones are mid-flight; failed ones are
  // intentionally skipped per Joan's 2026-05-22 ruling (user retries inline).
  const idleCards = useMemo(
    () => cards.filter((c) => c.status === "idle"),
    [cards],
  );

  const allIdleCardsValid = useMemo(
    () => idleCards.every((c) => c.name.trim().length > 0),
    [idleCards],
  );

  return {
    cards,
    updateCard,
    appendFilesToCard,
    addCards,
    removeCard,
    dropEmptyLeadingCards,
    reset,
    totalFileCount,
    idleCards,
    allIdleCardsValid,
    /** Backwards-compat alias: kept for callers that pre-date the lifecycle
     *  state machine. New code should prefer `allIdleCardsValid` so submitted
     *  cards don't gate the form. */
    allCardsValid: allIdleCardsValid,
  };
}
